import React from 'react';

import { StyleSheet, Text, View } from 'react-native';

import PickupTimeSelector from 'components/pickup-time-selector';
import { PickupNowEtaIndicator } from 'components/pickup-time-selector/pickup-now-eta-indicator';
import { ActionButton } from 'components/ui/action-button';
import {
  useCheckoutLoadingState,
  useServiceModeWidget,
} from 'experiments/checkout-v2/treatment/features/checkout/hooks';
import { PickupTimeHeadingContainer } from 'pages/cart/details/styled';
import SectionHeading from 'pages/cart/section-heading';
import { t } from 'state/intl/translations';
import { useServiceModeContext } from 'state/service-mode';
import { primitive } from 'styles/constants/primitives';

import { WidgetBaseStyles } from '../../pages/styled';

import { ServiceModeSelector } from './components/service-mode-selector';
import { VehicleDescriptionMobilePickup } from './components/vehicle-description-mobile-pickup';
import { IconStyled } from './styled';

export const ServiceModeWidget: React.FC<React.PropsWithChildren> = () => {
  const {
    selectedServiceMode,
    selectedServiceModeAccessibilityLabel,
    serviceModeOptions,
    handleOpenServiceModeSelector,
    handleSelectServiceMode,
    isServiceModeSelectorOpen,
    setIsServiceModeSelectorOpen,
    setVehicleDescription,
    vehicleDescriptionValue,
    vehicleDescriptionHasError,
    displayVehicleDescription,
    isOrderTimedFire,
    isDisabled,
  } = useServiceModeWidget();
  const { isDelivery } = useServiceModeContext();
  const { isCheckoutLoading } = useCheckoutLoadingState();

  if (!selectedServiceMode?.isAvailable) {
    return null;
  }

  return (
    <>
      <View>
        <View style={[styles.container, isDisabled && styles.disabledContainer]}>
          <View style={styles.serviceModeRow}>
            <IconStyled
              variant={selectedServiceMode?.icon}
              style={styles.icon}
              disabled={isDisabled}
            />
            <View style={styles.serviceModeDetails}>
              <Text
                accessible
                accessibilityRole="text"
                accessibilityLabel={selectedServiceModeAccessibilityLabel}
                style={[styles.serviceModeNameText, isDisabled && styles.disabledText]}
              >
                {selectedServiceMode?.heading}
              </Text>
              <View style={styles.space4} />
              <Text
                style={[styles.serviceModeDescriptionText, isDisabled && styles.disabledText]}
                numberOfLines={2}
              >
                {selectedServiceMode?.description}
              </Text>
            </View>
          </View>
          <ActionButton
            onPress={handleOpenServiceModeSelector}
            testID="change-service-mode-button"
            accessibilityLabel={t('changeServiceMode')}
            disabled={isDisabled || isCheckoutLoading}
          >
            {t('change')}
          </ActionButton>
        </View>
        <View style={styles.footerContainer}>
          <Text
            testID="service-modes-text"
            style={[styles.serviceModeOptionsText, isDisabled && styles.disabledText]}
          >
            {serviceModeOptions.map(serviceMode => serviceMode.heading).join(', ')}
          </Text>
        </View>
        {displayVehicleDescription && (
          <VehicleDescriptionMobilePickup
            onChange={setVehicleDescription}
            value={vehicleDescriptionValue}
            hasError={vehicleDescriptionHasError}
          />
        )}
        {isOrderTimedFire && (
          <View>
            <PickupTimeHeadingContainer>
              <SectionHeading>{isDelivery ? t('deliveryTime') : t('pickUpTime')}</SectionHeading>
              <PickupNowEtaIndicator />
            </PickupTimeHeadingContainer>
            <PickupTimeSelector isDisabled={isCheckoutLoading} />
          </View>
        )}
      </View>
      {serviceModeOptions && (
        <ServiceModeSelector
          isOpen={isServiceModeSelectorOpen}
          onClose={() => setIsServiceModeSelectorOpen(false)}
          serviceModeOptions={serviceModeOptions}
          handleSelectServiceMode={handleSelectServiceMode}
        />
      )}
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    ...WidgetBaseStyles.container,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  serviceModeDetails: {
    flexDirection: 'column',
  },
  serviceModeRow: {
    flexDirection: 'row',
  },
  serviceModeNameText: {
    fontSize: 16,
    lineHeight: 16,
    fontFamily: primitive.fontFamily.$headers,
    color: primitive.bk.$bbqBrown,
  },
  serviceModeDescriptionText: {
    lineHeight: 16,
    fontSize: 14,
    marginRight: 4,
    fontFamily: primitive.fontFamily.$body,
    color: primitive.bk.$bbqBrown,
  },
  icon: {
    marginRight: 4,
  },
  footerContainer: {
    paddingHorizontal: 16,
    paddingVertical: 4,
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10,
    backgroundColor: primitive.$blackOpacity4,
  },
  serviceModeOptionsText: {
    color: primitive.$blackOpacity70,
    fontSize: 10,
    fontFamily: primitive.fontFamily.$body,
    textAlign: 'right',
  },
  space4: {
    marginTop: 4,
  },
  disabledContainer: {
    backgroundColor: primitive.$blackOpacity4,
  },
  disabledText: {
    color: primitive.$blackOpacity50,
  },
});
